import { useEffect, useRef, useState } from 'react';
import * as THREE from 'three';
import useSceneStore from '../../stores/sceneStore';
import { normalizeRegion, regionFromQuad } from '../../utils/displayRegion';
import { sceneCameraRef } from '../../utils/sceneCameraRef';

// ============================================
// 展示区域框选遮罩层（DOM 级，2026-10-06）
// 不依赖画布内 r3f 拾取（任意漫游模式/叠加层下都可用）：
// - 覆盖在整个视口上（cursor: crosshair），左键拖拽画真实 DOM 虚线框（肉眼即时可见）
// - 松手：屏幕两端点经相机射线投影到地面 y=0 → normalizeRegion → setDisplayRegion
// - Esc 取消；过小（<2m）/射线未命中地面则取消
// ============================================

interface Props {
  /** 视口容器（用于计算屏幕相对坐标；默认取自身 boundingBox） */
}

export default function RegionDragOverlay(_props: Props) {
  const rootRef = useRef<HTMLDivElement>(null);
  const [rect, setRect] = useState<{ x1: number; y1: number; x2: number; y2: number } | null>(null);
  const draggingRef = useRef(false);
  const startRef = useRef<{ x: number; y: number } | null>(null);

  // Esc 取消
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        draggingRef.current = false;
        startRef.current = null;
        setRect(null);
        useSceneStore.getState().setRegionSelecting(false);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  /** 屏幕坐标 → 地面 y=0 世界坐标（相机射线求交；未命中返回 null） */
  const toGround = (clientX: number, clientY: number): { x: number; z: number } | null => {
    const cam = sceneCameraRef.get();
    const el = rootRef.current;
    if (!cam || !el) return null;
    const r = el.getBoundingClientRect();
    const ndc = new THREE.Vector2(
      ((clientX - r.left) / Math.max(1, r.width)) * 2 - 1,
      -((clientY - r.top) / Math.max(1, r.height)) * 2 + 1,
    );
    const raycaster = new THREE.Raycaster();
    raycaster.setFromCamera(ndc, cam);
    const plane = new THREE.Plane(new THREE.Vector3(0, 1, 0), 0);
    const hit = new THREE.Vector3();
    return raycaster.ray.intersectPlane(plane, hit) ? { x: hit.x, z: hit.z } : null;
  };

  const relPos = (clientX: number, clientY: number) => {
    const r = rootRef.current?.getBoundingClientRect();
    return { x: clientX - (r?.left ?? 0), y: clientY - (r?.top ?? 0) };
  };

  const onPointerDown = (e: React.PointerEvent) => {
    if (e.button !== 0) return;
    e.preventDefault();
    (e.currentTarget as HTMLElement).setPointerCapture?.(e.pointerId);
    draggingRef.current = true;
    const p = relPos(e.clientX, e.clientY);
    startRef.current = p;
    setRect({ x1: p.x, y1: p.y, x2: p.x, y2: p.y });
  };

  const onPointerMove = (e: React.PointerEvent) => {
    if (!draggingRef.current || !startRef.current) return;
    const p = relPos(e.clientX, e.clientY);
    setRect({ x1: startRef.current.x, y1: startRef.current.y, x2: p.x, y2: p.y });
  };

  const finish = (e: React.PointerEvent) => {
    if (!draggingRef.current || !startRef.current) return;
    draggingRef.current = false;
    const s = startRef.current;
    startRef.current = null;
    const eRel = relPos(e.clientX, e.clientY);
    setRect(null);
    const r = rootRef.current?.getBoundingClientRect();
    const L = r?.left ?? 0;
    const T = r?.top ?? 0;
    // 屏幕矩形四角（左上→右上→右下→左下）投到地面 → 四边形区域（贴合所见）；任一角未命中则回退对角 AABB
    const c1 = toGround(L + s.x, T + s.y);
    const c2 = toGround(L + eRel.x, T + s.y);
    const c3 = toGround(L + eRel.x, T + eRel.y);
    const c4 = toGround(L + s.x, T + eRel.y);
    const st = useSceneStore.getState();
    let region = c1 && c2 && c3 && c4 ? regionFromQuad([c1, c2, c3, c4]) : null;
    if (!region) {
      const a = toGround(L + s.x, T + s.y);
      const b = toGround(L + eRel.x, T + eRel.y);
      if (a && b) region = normalizeRegion(a.x, a.z, b.x, b.z);
    }
    if (region) st.setDisplayRegion(region);
    st.setRegionSelecting(false);
  };

  const box = rect
    ? {
        left: Math.min(rect.x1, rect.x2),
        top: Math.min(rect.y1, rect.y2),
        width: Math.abs(rect.x2 - rect.x1),
        height: Math.abs(rect.y2 - rect.y1),
      }
    : null;

  return (
    <div
      ref={rootRef}
      data-testid="region-drag-overlay"
      style={{ position: 'absolute', inset: 0, zIndex: 315, cursor: 'crosshair', pointerEvents: 'auto', userSelect: 'none' }}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={finish}
    >
      {box && (
        <div
          data-testid="region-drag-rect"
          style={{
            position: 'absolute',
            left: box.left,
            top: box.top,
            width: box.width,
            height: box.height,
            border: '2px dashed #faad14',
            background: 'rgba(250,173,20,0.10)',
            boxSizing: 'border-box',
            pointerEvents: 'none',
          }}
        />
      )}
    </div>
  );
}
